const FALLBACK_SPOTIFY_ARTIST_IDS: Record<string, string> = {
  caltucky: "2RUohzj4685KPlV24ebPDF",
};

const STORAGE_KEY = "americana:spotifyArtistIds";
const STORAGE_EVENT = "spotify-artist-map-changed";

const envSpotifyArtistMap = parseSpotifyArtistMap(import.meta.env.VITE_SPOTIFY_ARTIST_IDS);

const isBrowser = typeof window !== "undefined";

function getWindow(): Window | undefined {
  return isBrowser ? window : undefined;
}

function normalizeArtistKey(value?: string | null): string | null {
  if (!value) return null;
  const normalized = value.trim().toLowerCase();
  return normalized || null;
}

export function parseSpotifyArtistMap(rawValue: string | undefined): Record<string, string> {
  if (!rawValue) return {};
  const trimmed = rawValue.trim();
  if (!trimmed) return {};

  try {
    const parsed = JSON.parse(trimmed) as Record<string, unknown>;
    return Object.entries(parsed).reduce<Record<string, string>>((acc, [key, value]) => {
      if (typeof key === "string" && typeof value === "string" && key.trim() && value.trim()) {
        acc[key.trim().toLowerCase()] = value.trim();
      }
      return acc;
    }, {});
  } catch {
    return trimmed.split(",").reduce<Record<string, string>>((acc, entry) => {
      if (!entry) return acc;
      const [slug, spotifyId] = entry.split(":").map((part) => part.trim());
      if (slug && spotifyId) {
        acc[slug.toLowerCase()] = spotifyId;
      }
      return acc;
    }, {});
  }
}

function coerceMap(map: Record<string, string>): Record<string, string> {
  return Object.entries(map).reduce<Record<string, string>>((acc, [key, value]) => {
    const normalizedKey = normalizeArtistKey(key);
    if (!normalizedKey) return acc;
    const trimmedValue = value?.trim();
    if (!trimmedValue) return acc;
    acc[normalizedKey] = trimmedValue;
    return acc;
  }, {});
}

export function getStoredSpotifyArtistMap(): Record<string, string> {
  const w = getWindow();
  if (!w) return {};
  const raw = w.localStorage.getItem(STORAGE_KEY);
  if (!raw) return {};
  try {
    const parsed = JSON.parse(raw) as Record<string, string>;
    return coerceMap(parsed);
  } catch {
    return {};
  }
}

function persistSpotifyArtistMap(map: Record<string, string>): Record<string, string> {
  const w = getWindow();
  if (!w) return map;
  try {
    w.localStorage.setItem(STORAGE_KEY, JSON.stringify(map));
  } catch {
    /* ignore storage quota errors */
  }
  const detail = { ...map };
  w.dispatchEvent(new CustomEvent<Record<string, string>>(STORAGE_EVENT, { detail }));
  return map;
}

export function setStoredSpotifyArtistId(artistId: string, spotifyId: string | null): Record<string, string> {
  const normalizedKey = normalizeArtistKey(artistId);
  if (!normalizedKey) return getStoredSpotifyArtistMap();
  const map = { ...getStoredSpotifyArtistMap() };
  if (!spotifyId || !spotifyId.trim()) {
    delete map[normalizedKey];
    return persistSpotifyArtistMap(map);
  }
  map[normalizedKey] = spotifyId.trim();
  return persistSpotifyArtistMap(map);
}

export function resolveSpotifyArtistId(
  artistId?: string | null,
  overrides?: Record<string, string>,
): string | null {
  const normalizedKey = normalizeArtistKey(artistId);
  if (!normalizedKey) return null;
  const sources = [
    overrides ?? {},
    getStoredSpotifyArtistMap(),
    envSpotifyArtistMap,
    FALLBACK_SPOTIFY_ARTIST_IDS,
  ];
  for (const source of sources) {
    const candidate = source[normalizedKey];
    if (candidate) {
      return candidate;
    }
  }
  return null;
}

export function subscribeSpotifyArtistMap(
  handler: (map: Record<string, string>) => void,
): () => void {
  const w = getWindow();
  if (!w) {
    return () => {};
  }

  const storageHandler = (event: StorageEvent) => {
    if (event.key !== STORAGE_KEY) return;
    handler(getStoredSpotifyArtistMap());
  };

  const customHandler: EventListener = (event) => {
    const detail = (event as CustomEvent<Record<string, string>>).detail;
    if (detail) {
      handler(detail);
    } else {
      handler(getStoredSpotifyArtistMap());
    }
  };

  w.addEventListener("storage", storageHandler);
  w.addEventListener(STORAGE_EVENT, customHandler);

  return () => {
    w.removeEventListener("storage", storageHandler);
    w.removeEventListener(STORAGE_EVENT, customHandler);
  };
}

export function buildSpotifySearchUrl(query?: string | null): string {
  const trimmed = query?.trim();
  if (!trimmed) {
    return "https://open.spotify.com/search";
  }
  return `https://open.spotify.com/search/${encodeURIComponent(trimmed)}`;
}

export function extractSpotifyArtistId(input: string): string {
  const trimmed = input.trim();
  if (!trimmed) return "";
  const urlMatch = trimmed.match(/spotify\.com\/artist\/([a-zA-Z0-9]+)/i);
  if (urlMatch?.[1]) {
    return urlMatch[1];
  }
  const uriMatch = trimmed.match(/spotify:artist:([a-zA-Z0-9]+)/i);
  if (uriMatch?.[1]) {
    return uriMatch[1];
  }
  return trimmed;
}

export { FALLBACK_SPOTIFY_ARTIST_IDS, STORAGE_KEY as SPOTIFY_ARTIST_STORAGE_KEY };
